(ns app.front.form.render-edit
  (:require [app.domain.form.form-editor-util :as form-editor-util]
            [app.front.form.field-search :as field-search]
            [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.util :refer [for-frag] :as futil]
            [app.front.view.group.admin.form-schemas.form-attr-editors :as form-attr-editors]
            [app.front.view.group.admin.form-schemas.subs :as form-schemas-subs]
            [app.front.view.group.admin.form-schemas.util :as form-schemas-util]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [goog.string.format]
            [re-frame.core-instrumented :as rf]))

(defn add-child-element-evt [{:keys [type] :as form-element} select-element-after? order]
  [::form-schemas-subs/add-new-child-element
   form-element
   (form-schemas-util/type->default-child-type type)
   select-element-after?
   order])


(defn add-child-element-fn
  ([form-element]
   (add-child-element-fn form-element true nil))
  ([form-element select-element-after?]
   (add-child-element-fn form-element select-element-after? nil))
  ([form-element select-element-after? order]
   (fn [_]
     (>evt (add-child-element-evt form-element select-element-after? order)))))


(defn add-form-element-button [tr label-key form-element & [order]]
  (cc/button {:icon-position :right
              :kind          :tertiary
              :size          :small
              :icon          i/plus
              :on-click      (add-child-element-fn form-element true order)}
             (tr [label-key])))


(defn remove-form-element-button [tr label-key form-element props]
  (cc/button
    (merge {:icon-position :right
            :kind          :tertiary
            :size          :small
            :icon          i/remove
            :on-click      #(>evt [::form-schemas-subs/remove-form-element form-element])}
           props)
    (tr [label-key])))


(defn render-children-in-edit-view?
  "Do not render children for form-rows or sections in the 'form element edit view'"
  [{:keys [type]}]
  (-> form-schemas-util/container-types
      set
      (contains? type)
      not))


;;
;; Rendering for the 'structure edit' part
;;


(defn css-for-borders [{:keys [id] :as form-element}]
  (let [selected?          (<sub [::form-schemas-subs/id-is-selected? id])
        missing-attr-data? (not (form-schemas-util/required-fields-filled? form-element))
        conflicting-field? (seq (form-schemas-util/find-conflicting-fields :conflicting-field form-element))]
    (into ["border-solid border-2"]
          (cond
            selected? ["border-ui-blue"]
            (or missing-attr-data? conflicting-field?) ["border-ui-red"]
            :else ["border-ui-gray"]))))


(defn form-el->label [{:keys [type attributes] :as form-element}]
  (let [label-text        (cond
                            (:label attributes)
                            (:label attributes)

                            (form-schemas-util/attr-editable-for-type? type :text)
                            (:text attributes)

                            :else
                            (->> form-element
                                 form-schemas-util/form-el->non-proper-children
                                 first
                                 :text-value))
        max-chars-to-show 60
        char-count        (count label-text)
        truncate          #(-> % (subs 0 (min max-chars-to-show char-count)) (str "..."))]
    (cond-> (or label-text "")
      (> char-count max-chars-to-show)
      truncate)))


(defn render-children
  "Renders children of a form element.
  Result is a hiccup structure:
  [:<>
    [render-fn-for-child ctx prev-sibling-id next-sibling-id child-element]
    [render-fn-for-child ctx prev-sibling-id next-sibling-id child-element]
    ...])"
  [ctx form-element render-fn-for-child]
  (let [children (form-editor-util/form-el->children form-element)]
    (->> children
         (map-indexed (fn [i child]
                        [render-fn-for-child
                         ctx
                         (get-in children [(dec i) :id])
                         (get-in children [(inc i) :id])
                         child]))
         (into [:<>]))))


(defn drag-and-drop-config [path]
  {:on-drag-start (fn [e]
                    (.setData (.-dataTransfer e) "from-path" (str/join "," path)))
   :on-drag-over  #(.preventDefault %)
   :on-drag-enter #(.preventDefault %)
   :draggable     true
   :on-drop       (fn [e]
                    (let [from-path-as-string (-> (.getData (.-dataTransfer e) "from-path")
                                                  (str/split #","))
                          from-path           (mapv futil/parse-int from-path-as-string)]
                      (>evt [::form-schemas-subs/move-form-element from-path path])))})


(defn type->tr-key [type]
  (if (simple-keyword? type)
    (keyword "form.schema" type)
    type))


(defmulti
  ^{:arglists '([ctx prev-sibling-id next-sibling-id form-schema])}
  render
  "Accepts a context, previous-sibling-id, next-sibling-id and a form-schema.
  Returns schema edit section for the field type."
  (fn [_ _ _ schema] (:type schema)))


(defmethod render :form [{:keys [tr] :as ctx} _ _ {:keys [id] :as schema}]
  [:div {:key (str id "-root")}
   [:div.flex.flex-row.justify-end.mt-6.px-4
    [add-form-element-button tr :form.schema/add-section schema]]
   [render-children ctx schema render]])


(defn edit-actions-for-section [tr {:keys [id type order] :as form-element}]
  (let [gen-label-key (fn [s]
                        (->> (if (= type :subsection)
                               "-subsection"
                               "-section")
                             (str s)
                             (keyword "form.schema")))]
    [ui-select/select
     {:id           (str id "-section-edit")
      :select-text  (tr [(gen-label-key "select-action-for")])
      :allow-empty? false
      :class-name   "mr-2 ml-1 full-width-select"
      :values       [(when (= type :section)
                       {:update-fn (add-child-element-fn (<sub [::form-schemas-subs/root-element]) true (+ order 1))
                        :label-key :form.schema/add-section-below})
                     {:update-fn (add-child-element-fn form-element false)
                      :label-key :form.schema/add-row}
                     {:update-fn #(>evt [::form-schemas-subs/select-form-element-for-edit id])
                      :label-key (gen-label-key "edit")}
                     {:update-fn #(>evt [::form-schemas-subs/remove-form-element form-element])
                      :label-key (gen-label-key "remove")}]
      :val->str     #(tr [(:label-key %)])
      :on-change    #((:update-fn %))}]))

(defn order-controls
  [tr prev-sibling-id next-sibling-id path]
  [:div.flex.content-center
   (cc/button {:icon             i/arrow-up
               :kind             :ghost
               :size             :small
               :icon-position    :right
               :on-click         #(>evt [::form-schemas-subs/move-form-element
                                         path
                                         (conj (pop path) prev-sibling-id)])
               :class-name       "cursor-pointer only-icon-button"
               :disabled         (nil? prev-sibling-id)
               :icon-description (tr [:form.schema/move-section-up])}
              "")
   (cc/button {:icon             i/arrow-down
               :kind             :ghost
               :size             :small
               :icon-position    :right
               :on-click         #(>evt [::form-schemas-subs/move-form-element
                                         path
                                         (conj (pop path) next-sibling-id)])
               :class-name       "cursor-pointer only-icon-button ml-1"
               :disabled         (nil? next-sibling-id)
               :icon-description (tr [:form.schema/move-section-down])}
              "")])

(defmethod render :section [{:keys [tr] :as ctx}
                            prev-sibling-id
                            next-sibling-id
                            {:keys [id path] :as form-element}]
  [section/section
   (str id "-section")
   {:tr             tr
    :class          (css-for-borders form-element)
    :heading-button [:div.flex.flex-wrap.items-center.mt-2.mb-4
                     [edit-actions-for-section tr form-element]
                     [order-controls tr prev-sibling-id next-sibling-id path]]}
   (form-el->label form-element)
   [render-children ctx form-element render]])


(defmethod render :subsection [{:keys [tr] :as ctx} _ _ {:keys [id path] :as form-element}]
  [section/section
   (str id "-subsection")
   {:tr             tr
    :class          (css-for-borders form-element)
    :dnd-config     (merge (drag-and-drop-config path)
                           {:class "cursor-move"})
    :heading-button [:div.flex.justify-between.items-center
                     [edit-actions-for-section tr form-element]]}
   (form-el->label form-element)
   [render-children ctx form-element render]])


(defmethod render :form-row [{:keys [tr] :as ctx} _ _ {:keys [id path] :as form-element}]
  (let [drag-n-drop-config (drag-and-drop-config path)]
    [:div.rounded
     (merge (select-keys drag-n-drop-config [:on-drop :on-drag-over :on-drag-enter])
            {:class (conj (css-for-borders form-element) "bg-gray-200 mt-5 px-3 pt-4 pb-1")})
     [:header.mb-4
      [:div.flex.flex-col
       [:div.flex.mt-1.mb-2
        (merge (dissoc drag-n-drop-config :on-drop)
               {:class "cursor-move"})
        [:h3.mb-0.font-bold.text-base.flex-auto
         (tr [:form.schema/form-row])]
        (cc/icon {:icon        i/draggable
                  :description (tr [:form.schema/reorder-form-row])
                  :fill        "currentcolor"})]
       [:div.flex.flex-wrap.justify-end
        [remove-form-element-button tr :form.schema/remove-row form-element {:class-name "mr-2"}]
        (cc/button
          {:icon-position :right
           :class-name    "mr-2"
           :kind          :tertiary
           :size          :small
           :icon          i/pen
           :on-click      #(>evt [::form-schemas-subs/select-form-element-for-edit id])}
          (tr [:edit]))
        [add-form-element-button tr :form.schema/add-field form-element]]]]
     [render-children ctx form-element render]]))


(defmethod render :default [{:keys [tr]} prev-sibling-id next-sibling-id {:keys [id attributes type path] :as form-element}]
  (let [top-level? (= 2 (count path))
        required?  (:form.field/required attributes)]
    [:div.flex.flex-row.justify-between.items-center.bg-white.mt-4.rounded.mb-1
     (merge {:class    (conj (css-for-borders form-element) "cursor-move")
             :on-click #(>evt [::form-schemas-subs/select-form-element-for-edit id])}
            (drag-and-drop-config path))
     [:div.flex.flex-col.mb-2
      [:div.mt-1.mb-1.font-bold.ml-2
       {:class (when required? "required-field")}
       [form-el->label form-element]]
      [:div.mt-2-mb-2.ml-2 (tr [(type->tr-key type)])]]
     [:div.flex.gap-x-2
      (when top-level?
        [order-controls tr prev-sibling-id next-sibling-id path])
      [remove-form-element-button tr :remove form-element {:class-name "m-1"}]]]))


;;
;; Rendering for the selected form element
;;


(def types-with-long-content #{:p :sign-off/declaration-subsection})

(defn- editor-for-text-content [{:keys [tr]} {:keys [id type] :as form-element}]
  (let [text-elements      (->> form-element
                                (form-editor-util/form-el->children #(-> % :type (= :text)))
                                (map-indexed vector))
        text-element-count (count text-elements)]
    (for-frag [[i {:keys [text-value path]}] text-elements]
      [:div.mt-3
       [(if (types-with-long-content type) ui/text-area ui/text-input)
        {:id         (str id "-text-content")
         :value      text-value
         :label-text (cond-> (tr [:form.schema/text-content])
                       (> text-element-count 1) (str (inc i) ". "))
         :on-change  #(rf/dispatch-sync [::form-schemas-subs/edit-form-element path {:text-value (futil/tval %)}])}]])))


(defn editor-for-adding-child-el [tr {:keys [type] :as form-element}]
  (let [actual-child-elements (form-schemas-util/form-el->proper-children form-element)
        label-key             (case type
                                :checkbox-group :form.schema/checkboxes
                                :table :form.schema/table-columns
                                :radio-button-group :form.schema/radio-buttons
                                :structured-list :form.schema/list-rows
                                :list-row :form.schema/list-item
                                :form.schema/child-elements)]
    [:div.flex.mt-4.items-center
     [:div (str (tr [label-key]) ":")]
     [:div.ml-1.font-bold (str (count actual-child-elements))]
     (form-schemas-util/only-icon-button
       {:icon             i/plus
        :icon-description (tr [:form.schema/add-option])
        :class-name       "only-icon-button ml-1"
        :on-click         (add-child-element-fn form-element false)})]))


(defn uneditable-attrs-list [{:keys [type attributes]}]
  (let [uneditable-attrs (->> (form-schemas-util/uneditable-attrs-for-type attributes type)
                              (select-keys attributes))]
    (for-frag [attr uneditable-attrs]
      (let [[k v] attr]
        [:div.mt-3
         [ui/text-input
          {:id         (str k "-uneditable-attr")
           :value      v
           :disabled   true
           :label-text (some-> k str (subs 1) (str/replace #"-" " ") str/capitalize)}]]))))


(defn clear-existing-field-button [tr selected-element]
  (cc/button
    {:kind     :tertiary
     :size     :small
     :on-click #(>evt [::form-schemas-subs/set-selected-existing-field nil (:path selected-element)])}
    (tr [:form.schema/clear-existing-field])))


(defn select-existing-field-button [tr selected-element available-types]
  (cc/button
    {:kind     :tertiary
     :size     :small
     :on-click #(>evt [::field-search/form-field-search-toggle true (:path selected-element) available-types])}
    (tr [:form.schema/select-existing-field])))


(defn adding-new-field? [{:keys [new-field? type]}]
  (and new-field?
       (not= type :section)))


(defn declaration-subsection-order-control-data [{:keys [type path]} parent-type parent-element]
  (when (and (= type :sign-off/declaration-subsection)
             (= parent-type :sign-off/declaration-section-v2)
             path
             parent-element)
    (let [siblings (->> (form-editor-util/form-el->children parent-element)
                        (sort-by :order)
                        vec)
          idx      (->> siblings
                        (keep-indexed (fn [i sibling]
                                        (when (= (:path sibling) path)
                                          i)))
                        first)]
      (when (some? idx)
        {:path            path
         :prev-sibling-id (get-in siblings [(dec idx) :id])
         :next-sibling-id (get-in siblings [(inc idx) :id])}))))


(defn form-element-edit-view [{:keys [tr] :as ctx}
                              {:keys [type id] :as form-element}
                              parent-type
                              ancestor-is-selected?]
  (let [schema-type              (<sub [::form-schemas-subs/schema-type])
        tables-enabled?          (<sub [::ff/enabled? :feature-flag.id/form-editor-tables])
        proper-child-elements    (form-schemas-util/form-el->proper-children form-element)
        render-children?         (render-children-in-edit-view? form-element)
        changeable-types         (form-schemas-util/form-el->changeable-types parent-type form-element schema-type tables-enabled?)
        default-child-type       (form-schemas-util/type->default-child-type type)
        existing-field-selected? (<sub [::form-schemas-subs/existing-field-selected? (:path form-element)])
        parent-element           (<sub [::form-schemas-subs/form-element-by-path
                                        (form-editor-util/form-el->parent-path form-element)])
        order-control-data       (declaration-subsection-order-control-data form-element parent-type parent-element)]
    [:div.mt-1
     (when ancestor-is-selected?
       [:<>
        [:div.flex.flex-row.justify-between.items-center
         [:div.font-bold (form-el->label form-element)]
         [form-schemas-util/only-icon-button
          {:icon             i/remove
           :on-click         #(>evt [::form-schemas-subs/remove-form-element form-element])
           :class-name       "only-icon-button"
           :icon-description (tr [:remove])}]]
        (when (-> form-element :type (not= :list-row))
          (cond existing-field-selected?
                [clear-existing-field-button tr form-element]
                (adding-new-field? form-element)
                [select-existing-field-button tr form-element changeable-types]))])
     (when order-control-data
       [:div.mt-2.flex.justify-end
        [order-controls tr
         (:prev-sibling-id order-control-data)
         (:next-sibling-id order-control-data)
         (:path order-control-data)]])
     [:div.mt-1
      [ui-select/select
       {:id           (str id "-field-type-selector")
        :value        type
        :allow-empty? false
        :values       (sort-by #(tr [(type->tr-key %)]) changeable-types)
        :class-name   "full-width-select mt-2"
        :disabled     (> 2 (count changeable-types))
        :select-text  (tr [:form.schema/none])
        :label-text   (tr [:form.schema/field-type])
        :val->str     #(tr [(type->tr-key %)])
        :on-change    #(>evt [::form-schemas-subs/change-field-type form-element %])}]]
     [form-attr-editors/attr-editors-for-form-el ctx form-element]
     [editor-for-text-content ctx form-element]
     [uneditable-attrs-list form-element]
     (when render-children?
       [:<>
        (if (and default-child-type (not= :text default-child-type))
          [editor-for-adding-child-el tr form-element]
          (when (seq proper-child-elements)
            [:div.mt-3.mb-1 (tr [:form.schema/child-elements])]))
        (for-frag [child proper-child-elements]
          [:div.mt-1.mb-3
           {:class ["border-solid border-ui-black border-2 rounded"]}
           [:div.ml-2.mr-2.mb-3
            [form-element-edit-view ctx child type true]]])])]))


(defn configuration-box [{:keys [tr] :as ctx}]
  [:div.relative.p-2.w-full.lg:w-1-2
   [:div.border-solid.border-2.border-ui-gray.bg-white.rounded.p-8.sticky.overflow-y-auto
    {:style {:top        "80px"
             :max-height "calc(100vh - 200px)"}}
    (let [selected-element         (<sub [::form-schemas-subs/selected-form-element])
          existing-field-selected? (<sub [::form-schemas-subs/existing-field-selected? (:path selected-element)])
          parent-type              (-> (<sub [::form-schemas-subs/form-element-by-path
                                              (form-editor-util/form-el->parent-path selected-element)])
                                       :type)
          field-search-open?       (<sub [::field-search/form-field-search-open?])
          locked-when-signed-off?  (->> (<sub [::form-schemas-subs/locked-when-signed-off?])
                                        true?)]
      (if field-search-open?
        [field-search/form-field-search-view ctx]
        (if selected-element
          [:<>
           [:div.mr-1.mb-4.flex
            [:h3.font-bold.text-base.flex-1 (tr [:form.schema/field-configuration])]
            (cc/button
              {:icon-position :right
               :class-name    "mr-2"
               :kind          :tertiary
               :size          :small
               :icon          i/remove
               :on-click      #(>evt [::form-schemas-subs/select-form-element-for-edit false])}
              (tr [:close]))
            (cond existing-field-selected?
                  [clear-existing-field-button tr selected-element]
                  (adding-new-field? selected-element)
                  (select-existing-field-button tr selected-element nil))]
           [form-element-edit-view ctx selected-element parent-type false]]
          [:<>
           [:h3.font-bold.text-base.flex-1 (tr [:form.schema/form-configuration])]
           (cc/checkbox
             {:id         "locked-when-signed-off-btn"
              :label-text (tr [:form.schema/locked-when-signed-off])
              :checked    locked-when-signed-off?
              :on-change  #(>evt [::form-schemas-subs/change-locked-when-signed-off (not locked-when-signed-off?)])
              :class-name "mr-2 mt-1"})])))]])
